import { createCapture, startCapture, addFrame, stopCapture } from "./capture";
import perf from "./perf";
import { camera, viewBounds, worldToViewX, worldToViewY } from "./camera";
import { mapOverlay, drawFrame } from "./render";

const animations = [
  // { start, duration, from, to, cb }
];
const frameProcs = {};

// Run after the measurement reads but before the frame procs, for work that
// feeds them - draining inbound messages here means objects added this frame are
// laid out and drawn in the same frame rather than the next one.
const preFrameProcs = {};

const overlayFrameProcs = {};

const trackedOnOverlay = [];
const newAnimations = [];

// Geometry reads deferred to the start of a frame. Running them all before any
// writes means they hit a layout the browser already computed for the previous
// paint, instead of each one forcing a fresh flush part-way through the frame.
const pendingMeasurements = [];

function requestMeasurement(fn) {
  pendingMeasurements.push(fn);
}

function runMeasurements() {
  if (!pendingMeasurements.length) return;

  perf.begin("measure");
  for (let i = 0; i < pendingMeasurements.length; i++) {
    try {
      pendingMeasurements[i]();
    } catch (e) {
      console.error(e);
    }
  }
  pendingMeasurements.length = 0;
  perf.end("measure");
}

animations.add = (a) => newAnimations.push(a);

// Animations are only ever removed once their duration elapses, so a steadily
// climbing count means entries are outliving the elements they animate.
perf.counter("animations", () => animations.length);
perf.counter("tracked", () => trackedOnOverlay.length);

if (typeof window !== "undefined") {
  window.animations = animations;
  window.trackedOnOverlay = trackedOnOverlay;
  window.frameProcs = frameProcs;
}

let start, previousTimeStamp;

function processOverlay(delta) {
  const overlay = mapOverlay();

  const bounds = viewBounds();

  for (let i = 0; i < trackedOnOverlay.length; i++) {
    const track = trackedOnOverlay[i];

    // A track whose node has left the map takes its overlay content with it.
    // This used to be driven by a MutationObserver on #map, which only ever
    // worked because every node was a real DOM element.
    if (!track.node.isConnected) {
      track.removed(overlay);
      trackedOnOverlay.splice(i, 1);
      i--;
      continue;
    }

    const obj = track.obj;

    // A hidden object has no layout box, so getScreenCTM used to return null here
    // and the callback was skipped. Preserve that.
    if (!obj || obj.hidden) continue;

    const x = worldToViewX(obj.x);
    const y = worldToViewY(obj.y);

    // Tracked nodes sit at their object's origin, so the only scaling that
    // reaches them is the camera's and the object's own.
    const s = camera.scale * (obj.zoom || 1);

    track.callback(overlay, x, y, bounds, s, s);
  }

  for (const fpKey in overlayFrameProcs) {
    overlayFrameProcs[fpKey](
      overlay,
      bounds,
      leftBottomReserved(bounds),
      rightBottomReserved(bounds),
      delta
    );
  }
}

// Height kept clear at the bottom-left, for the filter panel.
//
// Reported by the panel itself rather than guessed, because its height depends on
// how many rows of role icons the estate needs — and a stale figure either wastes
// column or lets callouts run underneath it.
//
// This used to hold back 30% of the viewport height for the globe, which is
// commented out in BorgMap: a quarter of the left-hand callout column's range
// spent on an empty div, which pushed callouts for scopes low on the map up
// towards the middle.
let leftBottomReservedPx = 0;

function setLeftBottomReserved(px) {
  leftBottomReservedPx = px > 0 ? px : 0;
}

function leftBottomReserved(bounds) {
  // Never more than half the column, however short the viewport.
  return Math.min(leftBottomReservedPx, (bounds.y2 - bounds.y1) * 0.5);
}

// The same for the bottom-right, where the frame-rate readout, the effects cog
// and the renderer toggle share a strip. Reported by that strip rather than
// guessed, for the same reason as above.
let rightBottomReservedPx = 0;

function setRightBottomReserved(px) {
  rightBottomReservedPx = px > 0 ? px : 0;
}

function rightBottomReserved(bounds) {
  return Math.min(rightBottomReservedPx, (bounds.y2 - bounds.y1) * 0.5);
}

function setupOverlayTracking() {
  frameProcs.processOverlay = processOverlay;
}

// `obj` is the map object the node belongs to. Its position and zoom are what the
// overlay placement is derived from, so the node itself is only needed for the
// removal watch.
function trackOnOverlay(node, callback, removed, obj) {
  if (!frameProcs.processOverlay) setupOverlayTracking();
  const trackObj = { node, callback, removed, obj };
  trackedOnOverlay.push(trackObj);
  return trackObj;
}

function removeOverlayTrack(trackObj) {
  const i = trackedOnOverlay.indexOf(trackObj);
  if (i >= 0) trackedOnOverlay.splice(i, 1);
}

let capture = null;
let capturePending = false;
let framesToCapture = 1000000;
let framesCaptured = 0;

const keyFrames = {
  60: () => {
    window.setFocus("apps/DIA", true);
  },
  150: () => {
    window.setFocus("apps/BUFF", true);
  },
  240: () => {
    window.setFocus("apps/PCPL", true);
  },
  330: () => {
    window.setFocus("apps/EDEN", true);
  },
  420: () => {
    window.setFocus("", true);
  },
  510: () => {
    window.setFilter("envs", "Test");
  },
  600: () => {
    window.setFilter("envs", "Live");
  },
  690: () => {
    window.setFocus("apps/WILT");
    framesToCapture = 90;
  },
};

function animStep(timestamp) {
  try {
    if (capture == null && window.captureOption) {
      capture = createCapture();
      capturePending = true;
      const btn = document.createElement("button");
      btn.style = "position: absolute; top: 0; right: 0;";
      btn.innerText = "start capture";
      document.body.appendChild(btn);
      btn.addEventListener("click", () => {
        document.body.removeChild(btn);
        startCapture(capture, () => {
          console.log("capture starting");
          capturePending = false;
        });
      });
      return;
    }

    if (framesToCapture == 0 || capture == null || capturePending == true) {
      window.fakeTs = timestamp;
    } else {
      framesToCapture--;
      framesCaptured++;
      if (framesCaptured in keyFrames) {
        keyFrames[framesCaptured]();
      }
      addFrame(capture);
      if (framesToCapture == 0) {
        stopCapture(capture);
        capture = null;
      }
      window.fakeTs = (window.fakeTs || 0) + 1000 / 60;
    }

    animStepCore(window.fakeTs);
  } finally {
    window.requestAnimationFrame(animStep);
  }
}

// Frames actually painted, over a rolling half-second.
//
// Independent of the perf harness because the corner readout is there whether or
// not a session is being profiled, and off the wall clock rather than the frame
// timestamp because a capture drives that from a counter rather than from time.
let fpsFrames = 0;
let fpsSince = 0;
let fpsValue = 0;

function countFrame() {
  const now = performance.now();
  if (!fpsSince) fpsSince = now;

  fpsFrames++;
  const elapsed = now - fpsSince;
  if (elapsed < 500) return;

  fpsValue = (fpsFrames * 1000) / elapsed;
  fpsFrames = 0;
  fpsSince = now;
}

function currentFps() {
  return fpsValue;
}

function animStepCore(timestamp) {
  if (start === undefined) start = timestamp;

  countFrame();
  perf.beginFrame();

  const rawdelta = timestamp - previousTimeStamp;
  //const delta = Math.min(rawdelta, 1000); // 1fps min

  const delta = 1000 / 60;

  try {
    animStepBody(timestamp, delta);
  } finally {
    perf.endFrame();
  }

  previousTimeStamp = timestamp;
}

function animStepBody(timestamp, delta) {
  runMeasurements();

  for (const pfKey in preFrameProcs) {
    perf.begin(pfKey);
    try {
      preFrameProcs[pfKey](delta);
    } finally {
      perf.end(pfKey);
    }
  }

  for (const fpKey in frameProcs) {
    perf.begin(fpKey);
    try {
      frameProcs[fpKey](delta);
    } finally {
      perf.end(fpKey);
    }
  }

  animations.push(...newAnimations);
  newAnimations.length = 0; // apparently this is a fine way to empty an array

  perf.begin("animations");

  for (let i = 0; i < animations.length; i++) {
    const animation = animations[i];
    let remove = false;

    // Nothing dropped an animation before its duration elapsed, so one attached to
    // a removed element carried on writing to a detached node for the rest of its
    // life - and a scope's hue animation lasts ten minutes, long outliving the
    // scope itself. Entries with an owner end as soon as it leaves the document.
    if (animation.owner && !animation.owner.isConnected) {
      animations.splice(i, 1);
      i--;
      continue;
    }

    if (timestamp > animation.start + animation.duration) {
      try {
        animation.frame(animation.to, true, () => {});
      } catch (e) {
        console.error(e);
      }
      remove = true;
    } else if (timestamp >= animation.start) {
      try {
        let n = (timestamp - animation.start) / animation.duration;
        if (n < 0) n = 0;
        if (n > 1) n = 1;
        if (animation.easing) n = animation.easing(n);
        animation.frame(
          animation.from + (animation.to - animation.from) * n,
          false,
          () => {
            remove = true;
          }
        );
      } catch (e) {
        console.error(e);
      }
    }

    if (remove) {
      animations.splice(i, 1);
      i--;
    }
  }

  perf.end("animations");

  if (newAnimations.length) {
    animations.push(...newAnimations);
    newAnimations.length = 0; // apparently this is a fine way to empty an array
  }

  // Last, so the frame draws everything this pass produced. A no-op under the
  // SVG backend, where the browser owns painting.
  perf.begin("render");
  try {
    drawFrame();
  } finally {
    perf.end("render");
  }
}

window.requestAnimationFrame(animStep);

export {
  animations,
  previousTimeStamp,
  frameProcs,
  preFrameProcs,
  trackOnOverlay,
  removeOverlayTrack,
  overlayFrameProcs,
  requestMeasurement,
  setLeftBottomReserved,
  setRightBottomReserved,
  currentFps,
};
